import React, { useState } from 'react';
import { Send, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../i18n';

export const Contact = () => {
  const { toast } = useToast();
  const { copy } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    toast({
      title: copy.contact.inactiveTitle,
      description: copy.contact.inactiveText,
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-transparent to-red-50/50 dark:from-orange-900/10 dark:via-transparent dark:to-red-900/10"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-medium inline-block">
            {copy.contact.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold">
            {copy.contact.title}<span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">{copy.contact.accent}</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {copy.contact.intro}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <div className="space-y-8">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold">{copy.contact.questions}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {copy.contact.questionsText}
              </p>
            </div>

            {/* Creative work motif - code snippet style */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 p-8 border border-orange-500/20">
              <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-red-500/10 rounded-full blur-3xl"></div>
              
              <div className="relative space-y-4 font-mono text-sm">
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className="text-gray-400 ml-4">AntZone.js</span>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <span className="text-purple-400">const</span>{' '}
                    <span className="text-blue-400">collaboration</span>{' '}
                    <span className="text-white">=</span>{' '}
                    <span className="text-orange-400">async</span>{' '}
                    <span className="text-white">() =&gt; {'{'}</span>
                  </div>
                  <div className="pl-4">
                    <span className="text-purple-400">return</span>{' '}
                    <span className="text-white">{'{'}</span>
                  </div>
                  <div className="pl-8">
                    <span className="text-blue-300">idea</span>
                    <span className="text-white">:</span>{' '}
                    <span className="text-green-400">'{copy.contact.vision}'</span>
                    <span className="text-white">,</span>
                  </div>
                  <div className="pl-8">
                    <span className="text-blue-300">transformation</span>
                    <span className="text-white">:</span>{' '}
                    <span className="text-green-400">'{copy.contact.experience}'</span>
                    <span className="text-white">,</span>
                  </div>
                  <div className="pl-8">
                    <span className="text-blue-300">result</span>
                    <span className="text-white">:</span>{' '}
                    <span className="text-green-400">'{copy.contact.result}'</span>
                  </div>
                  <div className="pl-4">
                    <span className="text-white">{'}'}</span>
                  </div>
                  <div>
                    <span className="text-white">{'}'}</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-700">
                  <div className="text-gray-400">
                    <span className="text-green-500">✓</span> {copy.contact.ready}
                  </div>
                  <div className="text-gray-400 mt-1">
                    <span className="text-orange-500">→</span> {copy.contact.describe}
                  </div>
                </div>
              </div>
            </div>

            <Card className="border-2 border-orange-200 dark:border-orange-900 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20">
              <CardContent className="p-6 space-y-3">
                <h4 className="font-bold text-lg">{copy.contact.consultation}</h4>
                <p className="text-muted-foreground">
                  {copy.contact.consultationText}
                </p>
                <Button
                  type="button"
                  onClick={() => document.getElementById('name')?.focus()}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  {copy.contact.goToForm}
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    {copy.contact.labels[0]}
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={copy.contact.placeholders[0]}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    {copy.contact.labels[1]}
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={copy.contact.placeholders[1]}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="text-sm font-medium">
                    {copy.contact.labels[2]}
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={copy.contact.placeholders[2]}
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="subject" className="text-sm font-medium">
                    {copy.contact.labels[3]}
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={copy.contact.placeholders[3]}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium">
                    {copy.contact.labels[4]}
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={copy.contact.placeholders[4]}
                    required
                    rows={6}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white group"
                >
                  {copy.contact.submit}
                  <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
